const SEO_MANAGED_ATTR = 'data-seo-managed';

const ABSOLUTE_SITE_URL = 'https://astrodavido.com';
const DEFAULT_OG_IMAGE = `${ABSOLUTE_SITE_URL}/logo192.png`;
const SITE_NAME = 'AstroDavido Astrophotography';

const buildAbsoluteUrl = (value) => {
    if (!value) {
        return '';
    }

    if (/^https?:\/\//i.test(value)) {
        return value;
    }

    try {
        return new URL(value, window.location.origin || ABSOLUTE_SITE_URL).toString();
    } catch (error) {
        return new URL(value, ABSOLUTE_SITE_URL).toString();
    }
};

const upsertMetaTag = (selector, attributes) => {
    let metaTag = document.head.querySelector(selector);

    if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(SEO_MANAGED_ATTR, 'true');
        document.head.appendChild(metaTag);
    }

    Object.entries(attributes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            metaTag.removeAttribute(key);
            return;
        }

        metaTag.setAttribute(key, value);
    });
};

const upsertLinkTag = (selector, rel, href) => {
    let linkTag = document.head.querySelector(selector);

    if (!href) {
        if (linkTag) {
            linkTag.remove();
        }
        return;
    }

    if (!linkTag) {
        linkTag = document.createElement('link');
        linkTag.setAttribute(SEO_MANAGED_ATTR, 'true');
        document.head.appendChild(linkTag);
    }

    linkTag.setAttribute('rel', rel);
    linkTag.setAttribute('href', href);
};

const decodeHtmlEntities = (value) => {
    if (typeof document === 'undefined') {
        return String(value);
    }

    const textArea = document.createElement('textarea');
    textArea.innerHTML = value;
    return textArea.value;
};

export const stripHtml = (value) => {
    if (!value) {
        return '';
    }

    return decodeHtmlEntities(String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim());
};

export const truncateText = (value, maxLength = 160) => {
    const normalizedValue = stripHtml(value);

    if (normalizedValue.length <= maxLength) {
        return normalizedValue;
    }

    return `${normalizedValue.slice(0, maxLength - 1).trim()}…`;
};

export const applySeo = ({
    title,
    description,
    canonicalPath,
    ogTitle,
    ogDescription,
    ogImage,
    ogType = 'website',
    robots,
    structuredData
}) => {
    if (title) {
        document.title = title;
    }

    const canonicalUrl = canonicalPath ? buildAbsoluteUrl(canonicalPath) : '';
    const resolvedOgImage = buildAbsoluteUrl(ogImage || DEFAULT_OG_IMAGE);
    const resolvedDescription = description || '';
    const resolvedOgTitle = ogTitle || title || '';
    const resolvedOgDescription = ogDescription || resolvedDescription;

    upsertMetaTag('meta[name="description"]', {
        name: 'description',
        content: resolvedDescription
    });
    upsertMetaTag('meta[name="robots"]', {
        name: 'robots',
        content: robots || 'index,follow'
    });
    upsertMetaTag('meta[property="og:title"]', {
        property: 'og:title',
        content: resolvedOgTitle
    });
    upsertMetaTag('meta[property="og:description"]', {
        property: 'og:description',
        content: resolvedOgDescription
    });
    upsertMetaTag('meta[property="og:url"]', {
        property: 'og:url',
        content: canonicalUrl
    });
    upsertMetaTag('meta[property="og:type"]', {
        property: 'og:type',
        content: ogType
    });
    upsertMetaTag('meta[property="og:image"]', {
        property: 'og:image',
        content: resolvedOgImage
    });
    upsertMetaTag('meta[property="og:site_name"]', {
        property: 'og:site_name',
        content: SITE_NAME
    });
    upsertMetaTag('meta[name="twitter:card"]', {
        name: 'twitter:card',
        content: 'summary_large_image'
    });
    upsertMetaTag('meta[name="twitter:title"]', {
        name: 'twitter:title',
        content: resolvedOgTitle
    });
    upsertMetaTag('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: resolvedOgDescription
    });
    upsertMetaTag('meta[name="twitter:image"]', {
        name: 'twitter:image',
        content: resolvedOgImage
    });

    upsertLinkTag('link[rel="canonical"]', 'canonical', canonicalUrl);

    document.head
        .querySelectorAll('script[type="application/ld+json"][data-seo-managed="true"]')
        .forEach((scriptTag) => scriptTag.remove());

    if (structuredData) {
        const structuredDataEntries = Array.isArray(structuredData)
            ? structuredData
            : [structuredData];

        structuredDataEntries.forEach((entry) => {
            if (!entry) {
                return;
            }

            const scriptTag = document.createElement('script');
            scriptTag.type = 'application/ld+json';
            scriptTag.setAttribute(SEO_MANAGED_ATTR, 'true');
            scriptTag.textContent = JSON.stringify(entry);
            document.head.appendChild(scriptTag);
        });
    }
};

export const siteUrl = ABSOLUTE_SITE_URL;
export const toAbsoluteUrl = buildAbsoluteUrl;
export const siteName = SITE_NAME;
